import React from "react";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Lock, Users, History, KeyRound, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function LoginScreen() {
  const { loading, user } = useAuth();

  const signIn = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!loading && user) {
    window.location.replace("/dashboard");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#090d16] text-slate-100 grid-overlay relative overflow-hidden">
      {/* Decorative glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-cyan-500/10 blur-3xl" />

      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="font-display text-xl font-semibold tracking-tight">
            Vault<span className="text-emerald-400">Ops</span>
          </div>
        </div>
        <div className="text-xs font-mono text-slate-500 uppercase tracking-widest">
          v1.0 · Team Edition
        </div>
      </header>

      <main className="relative z-10 flex flex-col lg:flex-row items-center justify-between max-w-7xl mx-auto px-6 lg:px-12 gap-16 pt-10 pb-20">
        <div className="flex-1 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 text-xs font-mono uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Team Secret Vault
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-50">
            Ekibinizin tüm{" "}
            <span className="text-emerald-400">gizli bilgileri</span>
            <br />
            tek bir güvenli panelde.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
            Sunucu bağlantıları, VPN kullanıcıları, firewall ve iDRAC cihazları,
            domain ve lisans bilgileri — hepsini rol tabanlı erişim ve denetim
            kaydı ile birlikte yönetin.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button
              data-testid="google-login-button"
              onClick={signIn}
              size="lg"
              className="group h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-xl shadow-lg shadow-emerald-500/20"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
              </svg>
              Google ile Devam Et
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <div className="flex items-center gap-2 text-xs font-mono text-slate-500 uppercase tracking-widest">
              <Lock className="w-3.5 h-3.5" />
              End-to-end, Rol tabanlı, Audit log
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: KeyRound, label: "8 Kategori", sub: "Email · Sunucu · VPN · Firewall · iDRAC · Domain · Lisans · Diğer" },
              { icon: Users, label: "Rol Yönetimi", sub: "Admin ve Üye rolleri ile erişim kontrolü" },
              { icon: History, label: "Denetim Kaydı", sub: "Kim ne zaman ne yaptı — şeffaflık" },
            ].map((f) => (
              <div
                key={f.label}
                className="p-4 rounded-xl bg-[#111726]/80 border border-slate-800 hover:border-emerald-500/30 transition-colors"
              >
                <f.icon className="w-5 h-5 text-emerald-400 mb-2" />
                <div className="text-sm font-semibold text-slate-100">{f.label}</div>
                <div className="text-xs text-slate-400 leading-relaxed mt-1">{f.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 w-full max-w-md">
          <div className="relative rounded-2xl border border-slate-800 bg-[#0d121f]/80 backdrop-blur-xl p-6 shadow-2xl shadow-emerald-500/5">
            <div className="flex items-center gap-2 pb-4 border-b border-slate-800">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              </div>
              <div className="ml-2 text-xs font-mono text-slate-500">vaultops / production</div>
            </div>
            <div className="pt-4 space-y-3 font-mono text-xs">
              {[
                ["srv-prod-01.corp", "root", "••••••••••", "SSH"],
                ["fw-main.ap.lan", "admin", "••••••••", "Firewall"],
                ["idrac-node-04", "operator", "••••••••", "iDRAC"],
                ["ovh-domain.com", "N/A", "Lic: 2026-08-12", "Domain"],
              ].map(([h, u, p, t]) => (
                <div
                  key={h}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 rounded-lg bg-[#111726] border border-slate-800"
                >
                  <div>
                    <div className="text-slate-200">{h}</div>
                    <div className="text-slate-500 text-[11px] mt-0.5">
                      {u} · <span className="secret-dots">{p}</span>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-0.5">
                    {t}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
